import { uniqBy } from "lodash/array";

// should return a property object
export const getDefaultProperty = (business, saleyardId) => {
  /* Find a default property for a given saleyard.
  Generally this should only be used when within a sale context, and we have an explicit saleyard.
  Outside of this context (eg, edit business) we need to be aware there are potentially many saleyards at play. 
   */
  let result = null;
  if (business && business.properties) {
    if (business.properties.length === 1) {
      result = business.properties[0];
    } else if (business.defaultDestinations) {
      const default_destination_for_saleyard = business.defaultDestinations.find(
        destination => destination.saleyardId === saleyardId,
      );
      if (default_destination_for_saleyard) {
        result = business.properties.find(
          property =>
            property.id === default_destination_for_saleyard.propertyId,
        );
      }
    }
  }
  return result;
};

/**
 * Returns an alphabetically sorted, comma separated, string containing all of the unique (case insensitive) names for a Property
 *
 * @param {string} name The primary name for a Property
 * @param {array<string>} [additionalNames] An optional list of additional names for a Property
 * @return {string}
 */
function formatAdditionalPropertyNames(name, additionalNames) {
  if (!Array.isArray(additionalNames) || additionalNames.length === 0) {
    return name;
  }
  return uniqBy([name, ...additionalNames].filter(Boolean), name =>
    name.toLowerCase(),
  )
    .sort()
    .join(", ");
}

/**
 * Returns a formatted string containing a PIC and its list of associated names.
 *
 * @param {Property} [property] A Property
 * @param {array<string>} [additionalNames] An optional list of additional names for a Property
 * @param {string} [defaultValue=""] The value to be returned when there
 * @return {string}
 */
export function getPropertyDisplayName(
  property,
  additionalNames = [],
  defaultValue = "",
) {
  if (property) {
    const nameFormatted = formatAdditionalPropertyNames(
      property.name,
      additionalNames,
    );
    if (nameFormatted) {
      return `${property.PIC} (${nameFormatted})`;
    }
    return `${property.PIC}`;
  }
  return defaultValue;
}

const NLIS_LPA_PROGRAM_CODE = "LPA";
export const getLPAStatusFromPrograms = programs =>
  programs.find(program => program.code === NLIS_LPA_PROGRAM_CODE)?.status ||
  "";

export const isLpaAccredited = lpaCode => ["A", "A1"].includes(lpaCode);
